import { ReactNode, useEffect, useRef } from "react";
import { useImmer } from "use-immer";

import {
  addLoadMorePage,
  buildNavigationItems,
  buildSearchItems,
  type NavigationGroup,
  type NavigationGroupState,
  type NavigationItems,
  type NavigationResponse,
  type NavigationSearchResponse,
} from "@intelligems/ig-clients/storefront-navigation/navigation-items";

import { NAVIGATOR_INPUT_DEBOUNCE_MS } from "@src/features/experienceBuilder/toolbar/Navigator/constants";
import type { NavigatorLog } from "@src/features/experienceBuilder/toolbar/Navigator/storefront/NavigatorLog";
import {
  StorefrontChannelLockedError,
  type StorefrontClient,
  type StorefrontCredential,
} from "@src/features/experienceBuilder/toolbar/Navigator/storefront/StorefrontClient";
import {
  getStorefrontNavigationTarget,
  type ReadyStorefrontNavigationTarget,
  type StorefrontNavigationTarget,
} from "@src/features/experienceBuilder/toolbar/Navigator/storefront/getStorefrontNavigationTarget";
import {
  fetchBrowsePage,
  fetchLoadMorePage,
  fetchSearchPage,
} from "@src/features/experienceBuilder/toolbar/Navigator/storefront/navigationQueries";
import { useDebouncedSearch } from "@src/hooks/useDebouncedSearch";
import { useSelector } from "@src/store";
import { ExperienceBuilderSelector } from "@src/store/slices/experienceBuilder";

import { NavigatorContext } from "./NavigatorContext";
import { isUrlInput } from "./validateNavigableUrl";

type LoadedGroups = Partial<Record<NavigationGroup, NavigationGroupState>>;

/**
 * What a result belongs to. A result for any other stamp is stale: it is
 * discarded when it arrives, and it is not listed.
 */
interface Stamp {
  target: StorefrontNavigationTarget;
  /** The typed query. Undefined when the merchant browses. */
  query: string | undefined;
}

const sameStamp = (a: Stamp, b: Stamp) =>
  a.target === b.target && a.query === b.query;

/**
 * The first page for one stamp: the browse list when there is no query, or
 * the typed search. It is `unavailable` when the store gave no answer, which
 * is not the same as an answer with no items.
 */
type FirstPage = { stamp: Stamp } & (
  | { kind: "browse"; response: NavigationResponse }
  | { kind: "search"; response: NavigationSearchResponse }
  | { kind: "unavailable" }
);

/**
 * The credential of the next query to the target. Every target is read
 * tokenless until it answers that its Online Store channel is locked. Then
 * the own store uses the org's token. Undefined when the channel is locked
 * and the Navigator has no token for it: it sends no more queries.
 */
const credentialFor = (
  target: ReadyStorefrontNavigationTarget,
  channelLocked: boolean
): StorefrontCredential | undefined => {
  if (!channelLocked) {
    return { kind: "tokenless" };
  }
  const fallback = target.lockedChannelFallback;
  return fallback.kind === "storefrontToken"
    ? { kind: "storefrontToken", token: fallback.token }
    : undefined;
};

const NO_ITEMS: NavigationItems = {
  options: [],
  groupHasMore: {},
  groupEndCursor: {},
};

/**
 * The items of the first page and of "load more", in the Navigator's order.
 * While the first page loads, the browse list has only Home.
 */
const listItems = (
  root: string,
  firstPage: FirstPage | undefined,
  query: string | undefined,
  extraItems: LoadedGroups
): NavigationItems => {
  if (firstPage?.kind === "unavailable") {
    return NO_ITEMS;
  }
  return query === undefined
    ? buildNavigationItems({
        root,
        response: firstPage?.kind === "browse" ? firstPage.response : undefined,
        extraItems,
      })
    : buildSearchItems({
        root,
        response: firstPage?.kind === "search" ? firstPage.response : undefined,
        extraItems,
      });
};

/** The items that "load more" added, for one stamp. */
interface LoadMoreResult {
  stamp: Stamp;
  groups: LoadedGroups;
}

/**
 * One "load more" in flight. It carries the groups loaded before it, so its
 * result replaces them. Only one "load more" runs at a time. It is dropped
 * when its stamp stops being current.
 */
interface LoadMoreRequest {
  stamp: Stamp;
  group: NavigationGroup;
  cursor: string;
  loaded: LoadedGroups;
  /** The list's scroll position when the merchant clicked "load more". */
  scrollTop: number;
}

/**
 * The text in place of the list when the Navigator cannot search. Undefined
 * when the store answered: an empty list then shows the default "no results"
 * text.
 */
const noOptionsText = (
  target: StorefrontNavigationTarget,
  credential: StorefrontCredential | undefined,
  firstPage: FirstPage | undefined
) => {
  switch (target.status) {
    case "waitingForPreview":
      return "Waiting for preview…";
    case "previewReportIncomplete":
      return "Search is not available: the preview did not report its localization";
    case "unknownLocale":
      return `Search is not available: the preview reports an unknown language (${target.locale})`;
    case "unknownCountry":
      return `Search is not available: the preview reports an unknown country (${target.country})`;
    case "ready":
      if (!credential) {
        return "Search is not available: the store has a password";
      }
      return firstPage?.kind === "unavailable"
        ? "Search is not available: the store did not answer"
        : undefined;
  }
};

/**
 * The Navigator data source for the own store and for a demo URL: the
 * Storefront API in the Preview localization (ADR 0017). The browser calls
 * the Storefront API directly through the given `client`. No query is sent
 * until the preview reports its localization, and every query has
 * `@inContext` from it. Typed `http…` URLs navigate in every state.
 *
 * Every query is tokenless until the store answers that its Online Store
 * channel is locked. Then the same query runs again with the own store's
 * token, and so do all later queries for that target. A demo URL, or an own
 * store whose org has no `storefrontToken`, shows "the store has a password";
 * the own store also reports it to `log`, so that an engineer runs the token
 * backfill for the org. A new target starts again tokenless.
 */
export const StorefrontNavigatorProvider = ({
  client,
  log,
  children,
}: {
  client: StorefrontClient;
  log: NavigatorLog;
  children: ReactNode;
}) => {
  const path = useSelector(ExperienceBuilderSelector.getLastPath);
  const target = useSelector(getStorefrontNavigationTarget);
  const shopifyContext = useSelector(
    ExperienceBuilderSelector.getShopifyContext
  );
  const [inputValue, setInputValue] = useImmer("");
  const [firstPage, setFirstPage] = useImmer<FirstPage | undefined>(undefined);
  const [loadMoreResult, setLoadMoreResult] = useImmer<
    LoadMoreResult | undefined
  >(undefined);
  const [loadMoreRequest, setLoadMoreRequest] = useImmer<
    LoadMoreRequest | undefined
  >(undefined);
  // The target whose Online Store channel answered "locked".
  const [lockedTarget, setLockedTarget] = useImmer<
    StorefrontNavigationTarget | undefined
  >(undefined);
  const listboxRef = useRef<HTMLUListElement>(null);

  const debouncedInput = useDebouncedSearch(
    inputValue,
    NAVIGATOR_INPUT_DEBOUNCE_MS
  );
  // A URL is not a query. Suppress it when the live input or the lagging
  // debounced value is a URL: navigating on Enter clears the input while the
  // debounce still holds the URL.
  const query =
    isUrlInput(inputValue) || isUrlInput(debouncedInput)
      ? undefined
      : debouncedInput;
  const stamp: Stamp = { target, query };
  const channelLocked = lockedTarget === target;
  const credential =
    target.status === "ready"
      ? credentialFor(target, channelLocked)
      : undefined;

  // A new list keeps the scroll position, so the merchant stays at "load more".
  const restoreScroll = (scrollTop: number) => {
    requestAnimationFrame(() => {
      if (listboxRef.current) {
        listboxRef.current.scrollTop = scrollTop;
      }
    });
  };

  // A lock is seen once per target: after it, the Navigator sends the token,
  // or it sends nothing.
  const onChannelLocked = (target: ReadyStorefrontNavigationTarget) => {
    setLockedTarget(target);
    const fallback = target.lockedChannelFallback;
    if (fallback.kind !== "missingStorefrontToken") {
      return;
    }
    log.error(
      "[Navigator] The own store has a password and no storefrontToken: run the token backfill for this org",
      { organizationId: fallback.organizationId }
    );
  };

  // The preview sends `ig:shopify` on every page load, and each message is a
  // new context: this reports each incomplete message once, not on every
  // render.
  useEffect(() => {
    if (!shopifyContext || target.status !== "previewReportIncomplete") {
      return;
    }
    log.error(
      "[Navigator] ig:shopify has no Preview localization: check that the reverse proxy build reports country, locale, routes.root and shop",
      { missing: target.missing }
    );
  }, [log, target, shopifyContext]);

  useEffect(() => {
    if (target.status !== "ready") {
      return;
    }
    const credential = credentialFor(target, channelLocked);
    if (!credential) {
      return;
    }
    // A result that arrives after the target, the query or the credential
    // changed is discarded.
    let isCurrent = true;
    const stamp = { target, query };
    const queryTarget = { ...target, credential };
    const request: Promise<FirstPage> =
      query === undefined
        ? fetchBrowsePage(client, queryTarget).then((response) => ({
            stamp,
            kind: "browse",
            response,
          }))
        : fetchSearchPage(client, queryTarget, query).then((response) => ({
            stamp,
            kind: "search",
            response,
          }));
    request
      .then((page) => {
        if (isCurrent) {
          setFirstPage(page);
        }
      })
      .catch((error: unknown) => {
        if (!isCurrent) {
          return;
        }
        // A store with a password is an expected state. The lock does not
        // apply to a token request, so only a tokenless lock is handled.
        if (
          error instanceof StorefrontChannelLockedError &&
          credential.kind === "tokenless"
        ) {
          onChannelLocked(target);
          return;
        }
        log.error(
          query === undefined
            ? "[Navigator] Storefront browse query failed"
            : "[Navigator] Storefront search failed",
          { error }
        );
        setFirstPage({ stamp, kind: "unavailable" });
      });
    return () => {
      isCurrent = false;
    };
  }, [client, log, target, query, channelLocked, setFirstPage]);

  useEffect(() => {
    if (!loadMoreRequest) {
      return;
    }
    // A "load more" belongs to its stamp. When the stamp changes, the request
    // is dropped, so it is not sent again if the stamp comes back (the
    // merchant types text and clears it).
    const credential =
      target.status === "ready"
        ? credentialFor(target, channelLocked)
        : undefined;
    if (
      target.status !== "ready" ||
      !credential ||
      !sameStamp(loadMoreRequest.stamp, { target, query })
    ) {
      setLoadMoreRequest(undefined);
      return;
    }
    // A result that arrives after the stamp or the credential changed is
    // discarded.
    let isCurrent = true;
    const { group, cursor, loaded, scrollTop } = loadMoreRequest;
    fetchLoadMorePage(client, { ...target, credential }, query, group, cursor)
      .then((connection) => {
        if (!isCurrent) {
          return;
        }
        setLoadMoreResult({
          stamp: loadMoreRequest.stamp,
          groups: {
            ...loaded,
            [group]: addLoadMorePage({
              root: target.localization.root,
              group,
              loaded: loaded[group],
              connection,
            }),
          },
        });
      })
      .catch((error: unknown) => {
        if (!isCurrent) {
          return;
        }
        log.error("[Navigator] Storefront load more failed", { group, error });
      })
      .finally(() => {
        if (!isCurrent) {
          return;
        }
        setLoadMoreRequest(undefined);
        restoreScroll(scrollTop);
      });
    return () => {
      isCurrent = false;
    };
  }, [
    client,
    log,
    target,
    query,
    channelLocked,
    loadMoreRequest,
    setLoadMoreResult,
    setLoadMoreRequest,
  ]);

  const currentFirstPage =
    firstPage && sameStamp(firstPage.stamp, stamp) ? firstPage : undefined;
  const loadedGroups =
    loadMoreResult && sameStamp(loadMoreResult.stamp, stamp)
      ? loadMoreResult.groups
      : {};
  const loadingGroup =
    loadMoreRequest && sameStamp(loadMoreRequest.stamp, stamp)
      ? loadMoreRequest.group
      : undefined;

  const { options, groupHasMore, groupEndCursor } =
    target.status === "ready" && credential
      ? listItems(
          target.localization.root,
          currentFirstPage,
          query,
          loadedGroups
        )
      : NO_ITEMS;

  const onLoadMore = (group: NavigationGroup) => {
    const cursor = groupEndCursor[group];
    if (!cursor || loadingGroup) {
      return;
    }
    const scrollTop = listboxRef.current?.scrollTop ?? 0;
    setLoadMoreRequest({
      stamp,
      group,
      cursor,
      loaded: loadedGroups,
      scrollTop,
    });
    restoreScroll(scrollTop);
  };

  const selectedOption = options.find((opt) => opt.path === path) ?? null;

  return (
    <NavigatorContext.Provider
      value={{
        inputValue,
        setInputValue,
        options,
        selectedOption,
        isLoading: !!credential && !currentFirstPage,
        loadingGroup,
        showLoadMore: (group) => !!groupHasMore[group],
        onLoadMore,
        listboxRef,
        noOptionsText: noOptionsText(target, credential, currentFirstPage),
      }}
    >
      {children}
    </NavigatorContext.Provider>
  );
};
